import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import StarsIcon from "../../assets/icons/stars.svg?react";
import { cn } from "../../lib/utils";
import {
  useArticleVisualAbstracts,
  type VisualAbstractList,
} from "./useArticleVisualAbstracts";

/**
 * What the pill offers on this paper, or null when it must not render at all.
 *
 * `body === null` is every state that is not a settled answer — pending, error,
 * or the query disabled — and it returns null on purpose: an unresolved verdict
 * is not a licence to offer a poster, and failing open here would put the offer
 * back on the closed-access papers the gate exists to skip.
 *
 * `ready` and `failed` also return null. A paper that already has a poster is
 * served by the conversation's own card and by the header's poster sheet, and a
 * third affordance for the same object is the CTA fair this surface must not
 * become.
 */
export function visualAbstractPillAffordance(
  body: VisualAbstractList | null
): "generate" | "generating" | null {
  if (body === null) return null;
  if (body.eligibility.state === "ineligible") return null;
  const latest = body.artifacts.at(0);
  if (latest === undefined) return "generate";
  return latest.status === "queued" || latest.status === "generating"
    ? "generating"
    : null;
}

/**
 * The offer, in the article page's composer, before the reader has asked
 * anything.
 *
 * The conversation's card only reaches a reader who has already put a question
 * to the paper. This sits one screen earlier, in the row the mode picker
 * occupies on the JuisciGPT composer, so it is offered without competing with
 * the composer it shares a card with.
 *
 * It renders ONLY on a paper the API itself declares eligible, and stays absent
 * while that verdict is unknown. A dead CTA is not a neutral cost here: the
 * feature refuses a large share of papers — mostly for being closed-access —
 * and offering a poster that cannot exist is the frustration the eligibility
 * gate exists to prevent.
 */
export function VisualAbstractPill({
  articleId,
  articleTitle,
  roomId,
}: {
  articleId: string;
  /** Rides into the watch entry, so the completion toast names this paper. */
  articleTitle: string;
  roomId: string | null;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const { list, request } = useArticleVisualAbstracts({
    articleId,
    articleTitle,
    roomId,
  });
  const affordance = visualAbstractPillAffordance(list);

  if (affordance === null) return null;

  return (
    <button
      type="button"
      disabled={affordance === "generating"}
      onClick={() => {
        void request();
      }}
      className={cn(
        // Deliberately the mode trigger's shape: this sits in the same row of
        // the same composer, and the two reading as different kinds of control
        // would make the row look assembled rather than designed.
        "flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-xl py-1 pl-2.5 pr-3",
        "border border-solid border-ds-slate-200 bg-ds-white",
        "font-dm-sans font-medium text-ui-note text-ds-mint-900",
        "focus-visible:outline-none focus-visible:shadow-focus-ring",
        "disabled:cursor-not-allowed disabled:text-ds-slate-400"
      )}
    >
      {affordance === "generating" ? (
        <IconLoader2 size={16} className="animate-spin" />
      ) : (
        // The asset bakes its own grey into every path's fill AND stroke, so it
        // does not follow the text colour on its own. Recoloured here rather
        // than in the shared SVG: the four other call sites all render it grey
        // inside a light panel, and switching the asset to currentColor would
        // repaint every one of them.
        <StarsIcon className="[&_path]:fill-current [&_path]:stroke-current" />
      )}
      {affordance === "generating"
        ? t("Generating…")
        : t("Generate visual abstract")}
    </button>
  );
}
